import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

declare global {
  interface Window {
    hbspt?: {
      forms?: {
        create: (options: {
          region?: string;
          portalId: string;
          formId: string;
          target: string;
        }) => void;
      };
    };
  }
}

const scriptLoadPromises = new Map<string, Promise<void>>();

function hubspotScriptSrc(region: string) {
  if (region === 'eu1') return 'https://js-eu1.hsforms.net/forms/embed/v2.js';
  return 'https://js.hsforms.net/forms/embed/v2.js';
}

function loadScript(src: string): Promise<void> {
  if (window.hbspt?.forms?.create) return Promise.resolve();

  const existingPromise = scriptLoadPromises.get(src);
  if (existingPromise) return existingPromise;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
      once: true,
    });

    document.head.appendChild(script);
  });

  scriptLoadPromises.set(src, promise);
  return promise;
}

function ensureElementId(element: HTMLElement): string {
  if (element.id) return element.id;

  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(16).slice(2);

  element.id = `hubspot-form-${random}`;
  return element.id;
}

export default class HubspotFormController extends Controller {
  static values = {
    region: String,
    portalId: String,
    formId: String,
    scriptSrc: String,
  };

  declare readonly regionValue: string;
  declare readonly portalIdValue: string;
  declare readonly formIdValue: string;
  declare readonly scriptSrcValue: string;
  declare readonly hasScriptSrcValue: boolean;

  async connect() {
    const element = this.element as HTMLElement;

    if (element.dataset.hubspotFormRendered === 'true') return;
    if (!this.portalIdValue || !this.formIdValue) return;

    const region = this.regionValue || 'eu1';
    const src = this.hasScriptSrcValue ? this.scriptSrcValue : hubspotScriptSrc(region);

    await loadScript(src);

    const targetId = ensureElementId(element);

    window.hbspt?.forms?.create({
      region,
      portalId: this.portalIdValue,
      formId: this.formIdValue,
      target: `#${targetId}`,
    });

    element.dataset.hubspotFormRendered = 'true';
  }
}

application.register('hubspot-form', HubspotFormController);
